import { useState } from 'react'
import { api, ROLE_LABELS, type OnboardingStatus, type Role, type UseCase } from '../api'
import { Icon, type IconName } from './Icon'
import { because } from '../said'

/**
 * The guided first run: two questions between the sign-up form and the app.
 *
 * Sign-up used to end in the full editor, cold — a tree, a toolbar, and no
 * sense of what to do first. This asks the two things that actually change
 * what happens next: what the docs are for (stored on the workspace, so the
 * product can keep using the answer), and how the person wants to start. Two
 * steps, not seven; every choice is one click and lands in the real surface —
 * no tour, no sample project.
 *
 * It is skippable at every step and can never trap anybody: the answers are
 * persisted by the server, which is also the only judge of whether to show
 * this at all (`shouldOnboard` — the creating owner, before the run has
 * ended). A second browser, a reload, or a teammate joining by invitation
 * never sees it.
 *
 * Persistence never blocks the person. The use-case write is fired and
 * forgotten — it is guidance, not a gate — and the finishing write is awaited
 * with its failure swallowed: the worst outcome of a lost `done` is seeing
 * this screen once more, which beats wedging the first screen a customer ever
 * meets on an error dialog.
 */

const USE_CASES: { id: UseCase; icon: IconName; title: string; text: string }[] = [
  {
    id: 'product',
    icon: 'pages',
    title: 'Product docs',
    text: 'Guides and concepts that explain your product.',
  },
  {
    id: 'api',
    icon: 'braces',
    title: 'API reference',
    text: 'Endpoints, parameters, and examples that make them usable.',
  },
  {
    id: 'help',
    icon: 'info',
    title: 'Help centre',
    text: 'Answers your customers search before they write in.',
  },
  {
    id: 'handbook',
    icon: 'people',
    title: 'Internal handbook',
    text: 'How your team works, written down where it stays current.',
  },
]

/** What the second step promises, tuned by the first step's answer. */
/** What each kind of documentation is called in a sentence about it. */
const TEMPLATE_NAMES: Record<UseCase, string> = {
  product: 'product documentation site',
  api: 'API reference',
  help: 'help centre',
  handbook: 'handbook',
}

const WRITE_HINTS: Record<UseCase, string> = {
  product: 'Start with the quick-start guide — the page product docs are judged by.',
  api: 'Start with authentication — the page every API reference earns trust on.',
  help: 'Start with the question your customers ask most.',
  handbook: 'Start with the page every new teammate needs on day one.',
}

export function Onboarding({
  workspaceName,
  mayInvite,
  onStatus,
  onWrite,
  onTemplate,
  onImport,
  onUpgrade,
  onSkip,
}: {
  workspaceName: string
  /** False means the invitation is shown as a paid capability, without an unusable form. */
  mayInvite: boolean
  /**
   * The server's fresh answer after each write, so the app behind this
   * screen learns the use case in the SAME visit — the Home checklist tunes
   * its guidance to it, and waiting for a reload to know would waste the
   * one visit the answer was given on.
   */
  onStatus: (status: OnboardingStatus) => void
  /** Lands in the editor, on a draft. */
  onWrite: () => void
  /**
   * Writes the template pages on a draft and lands in the editor on it.
   *
   * The use case travels with the press rather than being read back from the
   * status: the answer was given seconds ago on this screen, and the app
   * behind it may still be holding the copy from before it.
   */
  onTemplate: (useCase: UseCase | null) => void
  /** Lands in the Import place. */
  onImport: () => void
  /** Finishes the run and opens the plan comparison. */
  onUpgrade: () => void
  /** Lands on Home, no questions asked. */
  onSkip: () => void
}) {
  const [step, setStep] = useState<1 | 2 | 3>(1)
  const [useCase, setUseCase] = useState<UseCase | null>(null)
  /** Which landing is in flight, so the chosen card says so and only once. */
  const [leaving, setLeaving] = useState<string | null>(null)
  /**
   * The start somebody picked, held until the third step lets go of it.
   *
   * Inviting used to be one of the four ways to START, which meant choosing
   * it was choosing NOT to have any pages: the person landed on an empty
   * roster with an empty site behind it. It is a step of its own now, after
   * the pages exist, and it can be skipped in one press.
   */
  const [start, setStart] = useState<{ id: string; land: () => void } | null>(null)
  const [invitee, setInvitee] = useState('')
  const [inviteRole, setInviteRole] = useState<Role>('editor')
  const [inviteState, setInviteState] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle')
  const [inviteError, setInviteError] = useState<string | null>(null)

  const choose = (picked: UseCase) => {
    setUseCase(picked)
    setStep(2)
    // Fired and forgotten: the answer tunes guidance, it does not gate the
    // person. A lost write costs a hint, not the workspace.
    void api
      .setOnboarding({ useCase: picked })
      .then(onStatus)
      .catch(() => undefined)
  }

  /**
   * Send the one invitation the third step asks for.
   *
   * A refusal is reported HERE and the run carries on: the pages are the
   * point of the first run, and a colleague's address typed wrongly must
   * never be the reason somebody's first visit ends without any.
   */
  const sendInvite = async () => {
    const email = invitee.trim()
    if (email === '') return
    setInviteState('sending')
    setInviteError(null)
    try {
      await api.invite({ email, role: inviteRole })
      setInviteState('sent')
    } catch (e) {
      setInviteState('failed')
      setInviteError(because(e, `Nothing was sent. You can invite them from People at any time.`))
    }
  }

  /** End the run on the server, then land. Never blocks on failure. */
  const finish = async (where: string, land: () => void) => {
    if (leaving) return
    setLeaving(where)
    const status = await api.setOnboarding({ done: true }).catch(() => null)
    if (status) onStatus(status)
    land()
  }

  const starts: { id: string; icon: IconName; title: string; text: string; land: () => void }[] = [
    {
      id: 'write',
      icon: 'page',
      title: 'Write',
      text: useCase
        ? WRITE_HINTS[useCase]
        : 'Open the editor on your starter page, on a draft — the live site never changes until you publish.',
      land: onWrite,
    },
    {
      /**
       * The answer to the previous question, used rather than merely noted.
       *
       * The first run asks what these docs are for and then only tuned a
       * sentence of hint text. Four or five pages in an order somebody thought
       * about is the difference between an empty site and a first week that
       * goes somewhere — and every page is a prompt rather than filler, so a
       * page nobody fills in reads as a gap instead of shipping as
       * documentation.
       */
      id: 'template',
      icon: 'pages',
      title: 'Start from a template',
      text: useCase
        ? `The pages a ${TEMPLATE_NAMES[useCase]} needs, on a draft — yours to rewrite.`
        : 'The pages this kind of documentation needs, on a draft — yours to rewrite.',
      land: () => onTemplate(useCase),
    },
    {
      id: 'import',
      icon: 'plus',
      title: 'Import your existing docs',
      text: 'GitBook, Docusaurus, Mintlify or a Markdown folder, onto a draft with an honest report.',
      land: onImport,
    },
  ]

  return (
    <div className="gate onboard" data-onboarding={step}>
      <div className="gate__identity">
        <span className="gate__mark">
          <Icon name="logo" size={26} />
        </span>
        <h1>{workspaceName}</h1>
        <p className="gate__tagline">
          {step === 1 ? 'Your workspace is ready.' : 'Everything here lands in the real thing.'}
        </p>
      </div>

      <div className="onboard__body">
        {/* Quiet marks, not a progress bar. All three remain visible on every
            plan: a paid capability should be discoverable before it is bought. */}
        {/*
          Two dots and the words behind them.

          The label used to be an `aria-label` on this div, which ARIA forbids
          on a generic element and which therefore announced nothing at all —
          the attribute was there, doing exactly nothing, which is the worst
          of both. Real text in the document works everywhere, needs no role,
          and is read in the order somebody meets it.
        */}
        <div className="onboard__steps">
          <span className="sr-only">Step {step} of 3</span>
          {[1, 2, 3].map((n) => (
            <span
              key={n}
              className={`onboard__dot${step === n ? ' onboard__dot--on' : ''}`}
              aria-hidden="true"
            />
          ))}
        </div>

        {step === 1 ? (
          <>
            <h2 className="onboard__ask">What are these docs for?</h2>
            <p className="onboard__sub">
              One click — it tunes the guidance you see. Nothing is locked in.
            </p>
            <div className="onboard__grid onboard__grid--four">
              {USE_CASES.map((option) => (
                <button
                  key={option.id}
                  className="onboard__card"
                  data-usecase={option.id}
                  onClick={() => choose(option.id)}
                >
                  <span className="onboard__cardicon">
                    <Icon name={option.icon} size={16} />
                  </span>
                  <span className="onboard__cardtitle">{option.title}</span>
                  <span className="onboard__cardtext">{option.text}</span>
                </button>
              ))}
            </div>
          </>
        ) : step === 3 ? (
          <>
            {/*
              THE THIRD STEP, and it ends in the editor.
              
              Documentation written alone is documentation that goes stale
              alone, so the invitation is asked for — once, here, where it
              costs one line — rather than left in a Settings screen nobody
              opens in week one. Skipping is one press and the pages are
              waiting either way.
            */}
            <h2 className="onboard__ask">Who else writes these docs?</h2>
            {mayInvite ? (
              <>
                <p className="onboard__sub">
                  One invitation now, or none — your pages are ready either way.
                </p>
                <form
                  className="onboard__invite"
                  onSubmit={(e) => {
                    e.preventDefault()
                    void sendInvite()
                  }}
                >
                  <label className="field">
                    <span className="label">Their email address</span>
                    <input
                      className="input"
                      type="email"
                      value={invitee}
                      data-autofocus
                      placeholder="colleague@example.com"
                      disabled={inviteState === 'sending' || leaving !== null}
                      onChange={(e) => {
                        setInvitee(e.target.value)
                        setInviteState('idle')
                        setInviteError(null)
                      }}
                    />
                  </label>
                  <label className="field">
                    <span className="label">What they may do</span>
                    <select
                      className="input"
                      value={inviteRole}
                      disabled={inviteState === 'sending' || leaving !== null}
                      onChange={(e) => setInviteRole(e.target.value as Role)}
                    >
                      {/* The same four words the roster uses — see `ROLE_LABELS`.
                          Owner is not offered: it is not something an invitation
                          may grant. */}
                      {(['editor', 'viewer', 'admin'] as Role[]).map((r) => (
                        <option key={r} value={r}>
                          {ROLE_LABELS[r]}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    className="btn btn--outline"
                    type="submit"
                    disabled={
                      invitee.trim() === '' || inviteState === 'sending' || leaving !== null
                    }
                  >
                    {inviteState === 'sending' ? <Icon name="spinner" size={13} /> : null}
                    {inviteState === 'sent' ? 'Invitation sent' : 'Send the invitation'}
                  </button>
                </form>
              </>
            ) : (
              <div className="onboard__invite" data-onboarding-invite-locked>
                <span className="onboard__cardicon">
                  <Icon name="lock" size={16} />
                </span>
                <p className="onboard__sub">
                  Free includes the seat you are using. Pro adds room for five people, with roles,
                  drafts and review kept separate from publishing.
                </p>
                <button
                  className="btn btn--outline"
                  disabled={leaving !== null}
                  data-onboarding-plans
                  onClick={() => void finish('plans', onUpgrade)}
                >
                  See plans
                </button>
              </div>
            )}
            {inviteError && (
              <p className="panel__error" role="alert">
                {inviteError}
              </p>
            )}
            <div className="onboard__land">
              <button
                className="btn btn--primary"
                disabled={leaving !== null}
                data-onboarding-land
                onClick={() => void finish(start?.id ?? 'write', start?.land ?? onWrite)}
              >
                {leaving ? <Icon name="spinner" size={13} /> : null}
                {inviteState === 'sent' ? 'Now open my pages' : 'Skip, and open my pages'}
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="onboard__ask">How do you want to start?</h2>
            <p className="onboard__sub">One click. No tour, no sample project.</p>
            <div className="onboard__grid">
              {starts.map((start) => (
                <button
                  key={start.id}
                  className="onboard__card"
                  data-start={start.id}
                  disabled={leaving !== null}
                  onClick={() => {
                    setStart({ id: start.id, land: start.land })
                    setStep(3)
                  }}
                >
                  <span className="onboard__cardicon">
                    <Icon name={start.icon} size={16} />
                  </span>
                  <span className="onboard__cardtitle">{start.title}</span>
                  <span className="onboard__cardtext">{start.text}</span>
                </button>
              ))}
            </div>
          </>
        )}

        <div className="onboard__foot">
          {step > 1 && (
            <button
              className="btn btn--ghost btn--sm"
              disabled={leaving !== null}
              onClick={() => setStep(step === 3 ? 2 : 1)}
            >
              Back
            </button>
          )}
          <button
            className="btn btn--ghost btn--sm"
            data-onboarding-skip
            disabled={leaving !== null}
            onClick={() => void finish('skip', onSkip)}
          >
            I’ll find my way
          </button>
        </div>
      </div>
    </div>
  )
}
